import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, Loader2, Trophy, Users, Zap } from "lucide-react";
import { api } from "../lib/api";

export default function AffiliatePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/affiliate/leaderboard?limit=10");
        setData(data);
      } catch (_e) {
        setData({ leaderboard: [], commission_pct: 30 });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const pct = data?.commission_pct ?? 30;

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-50" data-testid="affiliate-page">
      <header className="border-b border-zinc-200 dark:border-zinc-800 sticky top-0 bg-white/95 dark:bg-zinc-950/95 backdrop-blur z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-black text-lg">
            <span className="text-brand">⚡</span> CopyForge
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/login" className="hover:underline">Login</Link>
            <Link to="/register?intent=affiliate" data-testid="aff-signup-top" className="bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 px-3 py-1.5 font-bold hover:bg-zinc-700 dark:hover:bg-zinc-300">
              Join program
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-16 space-y-16">
        <section className="text-center">
          <div className="inline-flex items-center gap-2 bg-zinc-100 dark:bg-zinc-900 px-3 py-1.5 text-xs font-bold uppercase tracking-widest mb-5">
            <Award className="w-3 h-3" /> Affiliate program · Open
          </div>
          <h1 className="text-5xl md:text-6xl font-black tracking-tighter mb-5">
            Earn <span className="text-green-600 dark:text-green-400">{pct}%</span> for every customer you refer.
          </h1>
          <p className="text-lg md:text-xl text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto mb-8">
            CopyForge pays the highest commission in the AI copywriting space. Recurring revenue on subscriptions. One-time bumps on lifetime tiers. No caps.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/register?intent=affiliate"
              data-testid="aff-signup-hero"
              className="inline-flex items-center gap-2 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 px-8 py-4 font-bold text-lg hover:bg-zinc-700 dark:hover:bg-zinc-300"
            >
              <Zap className="w-5 h-5" /> Get my referral link (free)
            </Link>
          </div>
          <p className="mt-4 text-xs text-zinc-500">Free account = free referral code. Start sharing in 60 seconds.</p>
        </section>

        <section className="grid sm:grid-cols-3 gap-5">
          {[
            { num: `${pct}%`, label: "Commission on every sale", icon: Award },
            { num: "$149+", label: "Avg sale = $44+ per referral", icon: Trophy },
            { num: "Forever", label: "Lifetime cookie. Lifetime payouts.", icon: Users },
          ].map(({ num, label, icon: Icon }) => (
            <div key={label} className="border-2 border-zinc-900 dark:border-zinc-100 p-6">
              <Icon className="w-6 h-6 mb-3" />
              <div className="text-4xl font-black mb-1">{num}</div>
              <div className="text-sm text-zinc-600 dark:text-zinc-400">{label}</div>
            </div>
          ))}
        </section>

        <section>
          <h2 className="text-3xl font-black tracking-tighter mb-6 flex items-center gap-3">
            <Trophy className="w-7 h-7" /> Top affiliates this month
          </h2>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : data?.leaderboard?.length > 0 ? (
            <div className="border-2 border-zinc-900 dark:border-zinc-100 divide-y divide-zinc-200 dark:divide-zinc-800">
              {data.leaderboard.map((row) => (
                <div key={row.rank} data-testid={`leader-${row.rank}`} className="flex items-center justify-between px-5 py-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 flex items-center justify-center font-black text-lg ${row.rank <= 3 ? "bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900" : "border border-zinc-300 dark:border-zinc-700"}`}>
                      {row.rank}
                    </div>
                    <div>
                      <div className="font-bold">{row.name}</div>
                      <div className="text-xs text-zinc-500">{row.conversions} conversion{row.conversions === 1 ? "" : "s"}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-black text-lg">${(row.total_earnings_cents / 100).toFixed(2)}</div>
                    <div className="text-xs text-zinc-500">earned</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="border-2 border-dashed border-zinc-300 dark:border-zinc-700 p-12 text-center">
              <p className="text-zinc-500 mb-2">No leaderboard activity yet — be the first.</p>
              <p className="text-sm text-zinc-400">The first 10 affiliates to convert a customer get permanent leaderboard placement.</p>
            </div>
          )}
        </section>

        <section className="border-2 border-zinc-900 dark:border-zinc-100 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 p-8 md:p-12 text-center">
          <h2 className="text-3xl md:text-4xl font-black tracking-tighter mb-3">Ready to start earning?</h2>
          <p className="opacity-80 mb-6 max-w-xl mx-auto">
            Sign up for a free account. Grab your referral code from /referrals. Share it on Twitter, YouTube, your blog — wherever your audience hangs out.
          </p>
          <Link
            to="/register?intent=affiliate"
            data-testid="aff-signup-bottom"
            className="inline-flex items-center gap-2 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white px-8 py-4 font-bold text-lg"
          >
            Create my free affiliate account
          </Link>
        </section>
      </main>
    </div>
  );
}
